import app from 'flarum/forum/app';
// flarum/common/Component, NOT components/Component — see IndexHeader.tsx.
import Component from 'flarum/common/Component';
import Link from 'flarum/common/components/Link';
import Icon from 'flarum/common/components/Icon';
import CompatModal from './CompatModal';
import humanTime from 'flarum/common/helpers/humanTime';
import m from 'mithril';
/**
 * The compatibility board: every product's CURRENT live build, and what
 * BlizzNux Launcher users have reported about that exact build.
 *
 * 🚫 Every verdict here comes from real reports, counted per person by
 * /api/launcher/compatibility. A build nobody has reported on says exactly
 * that — never a guess, never a carried-over "works" from an older build
 * presented as current.
 */
export default class CompatBoard extends Component {
  loading = true;
  rows = [];

  /**
   * One request per page load, shared: the board is mounted twice (sidebar for
   * desktop, content column for phones — CSS shows one), and both read this.
   */
  /** The wiki article that explains the board. */
  static WIKI = '/wiki/the-compatibility-board-what-it-shows-and-how-it-counts';
  static pending = null;

  /**
   * The resolved rows, kept so a remount (coming Back to the front page)
   * renders the full board immediately instead of a 3-row skeleton that then
   * grows — on phones that growth shifts the list under the restored scroll.
   */
  static cached = null;
  static fetch() {
    if (!CompatBoard.pending) {
      CompatBoard.pending = app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/launcher/compatibility'
      }).then(res => CompatBoard.cached = res?.products || []).catch(() => {
        // Let the next mount try again rather than caching a failure.
        CompatBoard.pending = null;
        return [];
      });
    }
    return CompatBoard.pending;
  }
  oninit(vnode) {
    super.oninit(vnode);
    if (CompatBoard.cached) {
      this.rows = CompatBoard.cached;
      this.loading = false;
      return;
    }
    CompatBoard.fetch().then(rows => {
      this.rows = rows;
      this.loading = false;
      m.redraw();
    });
  }

  /** The compact stacked layout: the desktop sidebar and the phone drawer. */
  get side() {
    return this.attrs.variant === 'side' || this.attrs.variant === 'drawer';
  }
  view() {
    // Nothing to show is NOT a reason to render an empty frame on the front page.
    if (!this.loading && !this.rows.length) return null;
    return m("section", {
      className: 'BnCompat BnCompat--' + (this.attrs.variant || 'main') + (this.side ? ' BnCompat--compact' : '')
    }, m("div", {
      className: "BnCompat-head"
    }, m("h2", {
      className: "BnIndex-barTitle BnCompat-title"
    }, "Compatibility"), m(Link, {
      className: "BnCompat-how",
      href: CompatBoard.WIKI
    }, "How this works")), m("div", {
      className: "BnCompat-list"
    }, this.loading ? this.skeleton() : this.rows.map(r => this.row(r))));
  }
  skeleton() {
    return [0, 1, 2].map(i => m("div", {
      key: 's' + i,
      className: "BnCompat-row BnCompat-row--skeleton"
    }));
  }
  row(r) {
    const v = CompatBoard.verdict(r);
    return m(Link, {
      key: r.product,
      className: "BnCompat-row",
      href: r.url,
      title: 'Build history for ' + r.product,
      onclick: e => {
        // A plain click opens the modal; a modified click (new tab, new
        // window) falls through to the href and opens the page.
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        app.modal.show(CompatModal, {
          slug: r.slug,
          product: r.product
        });
      }
    }, m("span", {
      className: "BnCompat-product"
    }, m("span", {
      className: "BnCompat-name"
    }, r.product, m(Icon, {
      name: "fas fa-chevron-right",
      className: "BnCompat-more"
    })), m("span", {
      className: "BnCompat-build"
    }, "build ", r.build)), m("span", {
      className: 'BnCompat-verdict BnCompat-verdict--' + v.kind,
      "aria-label": v.label
    }, m(Icon, {
      name: v.icon
    }), this.side ? null : m("span", {
      className: "BnCompat-verdictLabel"
    }, v.label)), this.side ? null : m("span", {
      className: "BnCompat-detail"
    }, this.detail(r)));
  }
  detail(r) {
    const total = r.works + r.issues + r.broken;
    if (total) {
      const parts = [];
      if (r.works) parts.push(r.works + ' working');
      if (r.issues) parts.push(r.issues + ' with issues');
      if (r.broken) parts.push(r.broken + ' broken');
      return [parts.join(' · '), r.lastAt ? [' · ', humanTime(r.lastAt)] : null];
    }

    // No reports on THIS build. Say where things last stood, and label it as
    // an older build so it cannot be read as a verdict on the current one.
    if (r.lastGood) {
      return ['Last confirmed on build ' + r.lastGood.build, r.lastGood.at ? [' · ', humanTime(r.lastGood.at)] : null];
    }
    return this.side ? 'No reports yet' : 'No one has reported this build yet';
  }
  static verdict(r) {
    if (r.broken && !r.works && !r.issues) return {
      kind: 'broken',
      label: 'Broken',
      icon: 'fas fa-xmark'
    };
    if (r.issues || r.broken && r.works) return {
      kind: 'issues',
      label: 'Mixed',
      icon: 'fas fa-triangle-exclamation'
    };
    if (r.works) return {
      kind: 'works',
      label: 'Works',
      icon: 'fas fa-check'
    };
    return {
      kind: 'none',
      label: 'No reports',
      icon: 'fas fa-minus'
    };
  }
}
flarum.reg.add('blizznux-home', 'forum/components/CompatBoard', CompatBoard);